/**
 * ThemeContext — global Light/Dark mode toggle (Phase A)
 *
 * Light/Dark mode is purely a presentation concern. The toggle:
 *   - Adds/removes the `dark` class on <html> (Tailwind class-based dark mode
 *     is already enabled in `tailwind.config.js`).
 *   - Persists the user's preference in `localStorage` under
 *     `pfd-theme-preference`.
 *   - Falls back to OS-level `prefers-color-scheme` when no preference is set.
 *
 * No analysis logic / SSOT / credits / report wording is touched here.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'pfd-theme-preference';

const readInitialTheme = () => {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    /* ignore — fall through to light */
  }
  return 'light';
};

const applyThemeClass = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
    root.setAttribute('data-theme', 'dark');
  } else {
    root.classList.remove('dark');
    root.setAttribute('data-theme', 'light');
  }
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readInitialTheme());

  // Apply the class as early as possible — once on mount and on every change.
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage unavailable — still update in-memory */
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, isDark: theme === 'dark', setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Falling back to a stub keeps individual components renderable in tests
    // / storybook without forcing every consumer to wrap with a provider.
    return { theme: 'light', isDark: false, setTheme: () => {}, toggleTheme: () => {} };
  }
  return ctx;
}

export { STORAGE_KEY as THEME_STORAGE_KEY };
